/**
 * SPM Cart Upsell — quantity stepper, Add to Cart, and drawer/page lifecycle logic.
 *
 * This now works in TWO contexts on the same site: the cart drawer (present
 * on every page) and the standalone /cart page. Since drawer markup persists
 * globally, both contexts' tracking elements need unique, suffixed IDs
 * (SpmUpsellState-drawer vs SpmUpsellState-page, etc.) — otherwise they'd
 * collide as duplicate IDs whenever the standalone cart page is open.
 *
 * Key guards, both critical (confirmed via testing earlier in this project):
 *
 * 1. A shared "busy" flag prevents our own render-triggering functions from
 *    re-entering while one is already in flight — a render triggered by our
 *    own code is itself a DOM mutation, which re-fires the MutationObserver
 *    below, which can re-trigger the same logic again (infinite loop).
 *
 * 2. Every attribute write goes through spmMergeAndUpdateAttributes(), which
 *    fetches the CURRENT full attributes object first, merges in only the
 *    keys being changed, and sends the complete merged set. Sending a
 *    partial attributes object can silently drop sibling keys.
 */
(function () {
  var spmUpsellBusy = false;

  function spmMergeAndUpdateAttributes(newAttrs, options) {
    options = options || {};
    return fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var merged = Object.assign({}, cart.attributes || {}, newAttrs);
        var body = { attributes: merged };
        if (options.sections) {
          body.sections = options.sections;
          body.sections_url = window.location.pathname;
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      })
      .then(function (res) {
        return res.json();
      });
  }

  /* ── Context definitions ──────────────────────────────────────────────
     Each context knows its own tracking-element IDs, which sections to
     request, and how to paint the response back into the page. */

  /* ── Section fetching/rendering is now UNIFIED across both contexts ──
     Rather than each context only refreshing itself, every upsell-triggered
     write requests the full combined set of relevant sections (drawer +
     page, deduplicated) and attempts to paint both — each render function
     is a no-op if its target elements aren't present on the current page.
     This keeps the drawer and standalone /cart page in sync regardless of
     which one triggered the change. */

  function getAllRelevantSections() {
    var ids = [];
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.getSectionsToRender().forEach(function (s) {
        ids.push(s.id);
      });
    }
    var mainCartItemsEl = document.getElementById('main-cart-items');
    var mainCartFooterEl = document.getElementById('main-cart-footer');
    if (mainCartItemsEl && mainCartItemsEl.dataset.id) ids.push(mainCartItemsEl.dataset.id);
    if (mainCartFooterEl && mainCartFooterEl.dataset.id) ids.push(mainCartFooterEl.dataset.id);
    if (ids.indexOf('cart-icon-bubble') === -1) ids.push('cart-icon-bubble');
    return ids.filter(function (id, i) {
      return ids.indexOf(id) === i;
    });
  }

  function renderPageSections(response) {
    if (!response.sections) return;

    var cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl) {
      cartItemsEl.classList.toggle('is-empty', response.item_count === 0);
    }

    var mainCartItemsEl = document.getElementById('main-cart-items');
    if (mainCartItemsEl) {
      var itemsSectionId = mainCartItemsEl.dataset.id;
      if (itemsSectionId && response.sections[itemsSectionId]) {
        var parsedItems = new DOMParser().parseFromString(response.sections[itemsSectionId], 'text/html');
        var sourceItems = parsedItems.querySelector('.js-contents');
        var destItems = mainCartItemsEl.querySelector('.js-contents');
        if (sourceItems && destItems) destItems.innerHTML = sourceItems.innerHTML;
      }
    }

    var mainCartFooterEl = document.getElementById('main-cart-footer');
    if (mainCartFooterEl) {
      mainCartFooterEl.classList.toggle('is-empty', response.item_count === 0);
      var footerSectionId = mainCartFooterEl.dataset.id;
      if (footerSectionId && response.sections[footerSectionId]) {
        var parsedFooter = new DOMParser().parseFromString(response.sections[footerSectionId], 'text/html');
        var sourceFooter = parsedFooter.querySelector('.js-contents');
        var destFooter = mainCartFooterEl.querySelector('.js-contents');
        if (sourceFooter && destFooter) destFooter.innerHTML = sourceFooter.innerHTML;
      }
    }

    var bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl && response.sections['cart-icon-bubble']) {
      var parsedBubble = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
      var sourceBubble = parsedBubble.querySelector('.shopify-section');
      if (sourceBubble) bubbleEl.innerHTML = sourceBubble.innerHTML;
    }
  }

  function updateDrawerSectionsOnly(response) {
    if (!response.sections) return;
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl) return;

    var innerEl = document.getElementById('CartDrawer');
    var drawerHtml = response.sections['cart-drawer'];
    if (innerEl && typeof drawerHtml === 'string' && drawerHtml.length > 0) {
      var parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
      var source = parsed.querySelector('#CartDrawer');
      if (source) innerEl.innerHTML = source.innerHTML;
    } else if (innerEl) {
      console.error('SPM upsell: skipped drawer update, missing/empty cart-drawer section in response', response);
    }

    var bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl && response.sections['cart-icon-bubble']) {
      var parsedBubble = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
      var sourceBubble = parsedBubble.querySelector('.shopify-section');
      if (sourceBubble) bubbleEl.innerHTML = sourceBubble.innerHTML;
    }

    cartDrawerEl.classList.toggle('is-empty', response.item_count === 0);
  }

  function renderEverywhere(response) {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && response.sections) {
      var drawerHtml = response.sections['cart-drawer'];
      if (typeof drawerHtml === 'string' && drawerHtml.length > 0) {
        cartDrawer.renderContents(response);
        // Toggle, not just remove — matches Dawn's own native handling.
        // Previously this only ever removed the class (correct for "cart
        // just got its first item"), but never re-added it when a render
        // brought the cart back to empty, leaving the outer element's
        // is-empty state stale and out of sync with the actual cart.
        cartDrawer.classList.toggle('is-empty', response.item_count === 0);
      } else {
        console.error('SPM upsell: skipped drawer render, missing/empty cart-drawer section in response', response);
      }
    }
    renderPageSections(response);
  }

  function renderEverywhereWithoutOpeningDrawer(response) {
    // Same end result as renderEverywhere, but updates the drawer's content
    // silently rather than via cartDrawer.renderContents(), which always
    // opens the drawer as a side effect — undesirable when the customer is
    // interacting with the standalone /cart page, not the drawer.
    updateDrawerSectionsOnly(response);
    renderPageSections(response);
  }

  var contexts = {
    drawer: {
      stateId: 'SpmUpsellState-drawer',
      pickId: 'SpmUpsellPick-drawer',
      watchRootId: 'CartDrawer',
      render: renderEverywhere,
      getSections: getAllRelevantSections,
    },
    page: {
      stateId: 'SpmUpsellState-page',
      pickId: 'SpmUpsellPick-page',
      watchRootId: 'main-cart-items',
      render: renderEverywhereWithoutOpeningDrawer,
      getSections: getAllRelevantSections,
    },
  };

  /* ── Quantity stepper + Add to Cart (shared logic, context-aware) ──── */

  function initSpmUpsellPicker(container, ctx) {
    var picker = container.querySelector('.spm-upsell-picker');
    if (!picker) return;

    var trigger = picker.querySelector('.spm-upsell-picker__trigger');
    var list = picker.querySelector('.spm-upsell-picker__list');
    var options = picker.querySelectorAll('.spm-upsell-picker__option');
    var atcBtn = container.querySelector('.spm-upsell__atc');
    if (!trigger || !list || !atcBtn) return;

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var isOpen = list.classList.contains('is-open');
      document.querySelectorAll('.spm-upsell-picker__list.is-open').forEach(function (l) {
        l.classList.remove('is-open');
        var t = l.closest('.spm-upsell-picker').querySelector('.spm-upsell-picker__trigger');
        if (t) t.classList.remove('is-open');
      });
      if (!isOpen) {
        list.classList.add('is-open');
        trigger.classList.add('is-open');
      }
    });

    options.forEach(function (option) {
      option.addEventListener('click', function () {
        var entry = option.getAttribute('data-entry');
        var variantId = option.getAttribute('data-variant-id');
        var optImg = option.querySelector('.spm-upsell-picker__option-img');
        var optTitle = option.querySelector('.spm-upsell-picker__option-title');
        var optPrice = option.querySelector('.spm-upsell-picker__option-price');

        // Update the trigger to reflect the new selection.
        var triggerImg = trigger.querySelector('.spm-upsell-picker__trigger-img');
        var triggerTitle = trigger.querySelector('.spm-upsell-picker__trigger-title');
        var triggerPrice = trigger.querySelector('.spm-upsell-picker__trigger-price');
        if (optImg && triggerImg) {
          triggerImg.src = optImg.src;
          triggerImg.alt = optImg.alt;
        }
        if (optTitle && triggerTitle) triggerTitle.textContent = optTitle.textContent;
        if (optPrice && triggerPrice) triggerPrice.textContent = optPrice.textContent;

        options.forEach(function (o) {
          o.classList.remove('is-selected');
          o.setAttribute('aria-selected', 'false');
        });
        option.classList.add('is-selected');
        option.setAttribute('aria-selected', 'true');

        list.classList.remove('is-open');
        trigger.classList.remove('is-open');

        atcBtn.setAttribute('data-variant-id', variantId);

        // A manual selection is deliberate customer intent — lock it in
        // immediately as the new sticky recommendation, rather than waiting
        // for the passive mismatch check that normally handles this.
        var pickEl = document.getElementById(ctx.pickId);
        var rule = pickEl ? pickEl.getAttribute('data-rule') : '';
        spmMergeAndUpdateAttributes({
          upsell_locked_rule: rule,
          upsell_locked_key: entry,
        }).catch(function (e) {
          console.error('SPM upsell manual pick error:', e);
        });
      });
    });
  }

  document.addEventListener('click', function () {
    document.querySelectorAll('.spm-upsell-picker__list.is-open').forEach(function (l) {
      l.classList.remove('is-open');
      var t = l.closest('.spm-upsell-picker').querySelector('.spm-upsell-picker__trigger');
      if (t) t.classList.remove('is-open');
    });
  });

  function initSpmUpsell(container) {
    if (container.dataset.spmUpsellInit === 'true') return;
    container.dataset.spmUpsellInit = 'true';

    var ctx = container.closest('#CartDrawer') ? contexts.drawer : contexts.page;

    initSpmUpsellPicker(container, ctx);

    var qtyInput = container.querySelector('.spm-upsell__qty-input');
    var decreaseBtn = container.querySelector('[data-action="decrease"]');
    var increaseBtn = container.querySelector('[data-action="increase"]');
    var atcBtn = container.querySelector('.spm-upsell__atc');

    if (!qtyInput || !atcBtn) return;

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        if (val > 1) qtyInput.value = val - 1;
      });
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
      });
    }

    atcBtn.addEventListener('click', function () {
      var variantId = atcBtn.getAttribute('data-variant-id');
      var quantity = parseInt(qtyInput.value, 10) || 1;
      if (!variantId) return;

      var originalText = atcBtn.textContent;
      atcBtn.disabled = true;
      atcBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity }),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function () {
          return spmMergeAndUpdateAttributes(
            { used_upsell: 'true', used_upsell_variant_id: variantId },
            { sections: ctx.getSections() }
          );
        })
        .then(function (response) {
          ctx.render(response);
        })
        .catch(function (e) {
          console.error('SPM upsell add-to-cart error:', e);
          atcBtn.disabled = false;
          atcBtn.textContent = originalText;
        });
    });
  }

  function initAll() {
    document.querySelectorAll('.spm-upsell').forEach(initSpmUpsell);
  }

  /* ── Reset-on-removal and sticky-pick persistence (per context) ─────── */

  function checkUsedUpsellReset(ctx) {
    if (spmUpsellBusy) return;

    var stateEl = document.getElementById(ctx.stateId);
    if (!stateEl) return;

    var usedUpsell = stateEl.getAttribute('data-used-upsell') === 'true';
    var trackedVariantId = stateEl.getAttribute('data-used-upsell-variant-id');
    if (!usedUpsell || !trackedVariantId) return;

    var stillInCart = !!document.querySelector(
      '.quantity__input[data-quantity-variant-id="' + trackedVariantId + '"]'
    );
    if (stillInCart) return;

    spmUpsellBusy = true;

    spmMergeAndUpdateAttributes(
      { used_upsell: '', used_upsell_variant_id: '' },
      { sections: ctx.getSections() }
    )
      .then(function (response) {
        ctx.render(response);
      })
      .catch(function (e) {
        console.error('SPM upsell reset error:', e);
      })
      .finally(function () {
        spmUpsellBusy = false;
      });
  }

  function persistUpsellPick(ctx) {
    if (spmUpsellBusy) return;

    var stateEl = document.getElementById(ctx.stateId);
    var pickEl = document.getElementById(ctx.pickId);
    if (!stateEl) return;

    var storedRule = stateEl.getAttribute('data-locked-rule') || '';
    var storedKey = stateEl.getAttribute('data-locked-key') || '';
    var currentRule = pickEl ? pickEl.getAttribute('data-rule') : '';
    var currentKey = pickEl ? pickEl.getAttribute('data-key') : '';

    if (currentRule === storedRule && currentKey === storedKey) return;

    spmUpsellBusy = true;

    spmMergeAndUpdateAttributes({
      upsell_locked_rule: currentRule,
      upsell_locked_key: currentKey,
    })
      .catch(function (e) {
        console.error('SPM upsell persist error:', e);
      })
      .finally(function () {
        spmUpsellBusy = false;
      });
  }

  /* ── Bootstrapping ────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  function watchContext(ctx, attemptsRemaining) {
    if (typeof attemptsRemaining === 'undefined') attemptsRemaining = 15;
    var rootEl = document.getElementById(ctx.watchRootId);
    if (rootEl) {
      var observer = new MutationObserver(function () {
        initAll();
        checkUsedUpsellReset(ctx);
        persistUpsellPick(ctx);
      });
      observer.observe(rootEl, { childList: true, subtree: true });
    } else if (attemptsRemaining > 0) {
      // Cap retries — e.g. the page-context root only exists on /cart, so
      // this correctly gives up quickly on every other page rather than
      // polling forever.
      setTimeout(function () {
        watchContext(ctx, attemptsRemaining - 1);
      }, 200);
    }
  }

  watchContext(contexts.drawer);
  watchContext(contexts.page);
})();
